import React, { useCallback, useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { Box, Paper, Typography } from '@mui/material';

registerECharts([
  BarChart,
  LineChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Props = {
  option: EChartsOption;
  title: string;
  source: string;
  range: string;
  onSelect?: (value: { product: string; date?: string }) => void;
  height?: number;
  onClear?: () => void;
};

/** Report-owned chart surface matching the recovered blog dashboard composition. */
export default function BlogChart({ option, title, source: _source, range, onSelect, onClear, height = 340 }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const pendingOption = useRef<EChartsOption | undefined>(undefined);
  const syncChart = useCallback(() => {
    const node = element.current;
    const chart = instance.current;
    // A hidden or unmounting chart cannot be resized at zero size.
    // Keep its pending option until ResizeObserver reports a visible surface.
    if (!node?.clientWidth || !node.clientHeight || !chart || chart.isDisposed()) {
      return;
    }
    chart.resize();
    if (pendingOption.current) {
      chart.setOption(pendingOption.current, { notMerge: true });
      pendingOption.current = undefined;
    }
  }, []);
  const select = useRef(onSelect);
  const clear = useRef(onClear);
  useEffect(() => {
    select.current = onSelect;
    clear.current = onClear;
  }, [onSelect, onClear]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event: { seriesName?: string; name?: string }) => {
      if (event.seriesName) {
        select.current?.({ product: event.seriesName, date: event.name });
      }
    });
    chart.getZr().on('click', (event) => {
      if (!event.target) {
        clear.current?.();
      }
    });
    const observer = new ResizeObserver(syncChart);
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, [syncChart]);
  useEffect(() => {
    pendingOption.current = option;
    syncChart();
  }, [option, syncChart]);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', minWidth: 0, flexShrink: 0, minHeight: height + 25 }}>
      <Typography
        component="h2"
        variant="h6"
        sx={{
          p: '5px 6px',
          minHeight: 25,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: '0.02em',
          textAlign: 'center',
          bgcolor: '#0f464f',
          color: '#fff',
          fontFamily: `"Suisse Int'l", Arial, sans-serif`,
        }}
      >
        {title}
      </Typography>
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${range}. ${onSelect ? 'Select a series to filter.' : ''}`}
        sx={{ width: '100%', height, minHeight: height, flexShrink: 0, minWidth: 0 }}
      />
    </Paper>
  );
}
